import { motion, AnimatePresence } from 'framer-motion';
import { Link } from 'react-router-dom';
import { 
  ArrowRight,
  ChevronDown,
} from 'lucide-react';
import { useEffect, useState } from 'react';
import { PublicPageLayout } from '@/components/landing/PublicPageLayout';
import { Button } from '@/components/ui/button';
import { SEO } from '@/components/SEO';

import { TextGradientScroll } from '@/components/ui/text-gradient-scroll';

const fadeIn = {
  hidden: { opacity: 0, y: 20 },
  visible: { 
    opacity: 1, 
    y: 0, 
    transition: { duration: 0.8, ease: [0.25, 0.46, 0.45, 0.94] as const } 
  }
};

const stagger = {
  visible: { transition: { staggerChildren: 0.15 } }
};

const wordVariants = {
  hidden: { opacity: 0, y: 30 },
  visible: (i: number) => ({
    opacity: 1,
    y: 0,
    transition: {
      delay: i * 0.12,
      duration: 0.5,
      ease: [0.25, 0.46, 0.45, 0.94] as const
    }
  })
};




export default function About() {
  const headline = "We believe winning shouldn't be left to chance.";
  const words = headline.split(' ');
  const [showScrollIndicator, setShowScrollIndicator] = useState(true);
  

  useEffect(() => {
    const handleScroll = () => {
      setShowScrollIndicator(window.scrollY < 100);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  return (
    <PublicPageLayout>
      <SEO 
        title="About Sorvaine — AI Bid & Tender Management Software"
        description="Sorvaine is AI-powered bid and tender management software built for teams worldwide. Learn about our mission and the team behind the product."
        canonical="https://sorvaine.com/about"
      />

      {/* Hero Section - Premium with geometric grid */}
      <section className="min-h-[85vh] flex items-center pt-28 pb-16 relative animate-hero-gradient">
        {/* Decorative geometric grid */}
        <div className="absolute inset-0 geometric-grid opacity-30 pointer-events-none" />
        
        <div className="max-w-[1200px] mx-auto px-6 w-full relative z-10">
          <motion.div
            initial="hidden"
            animate="visible"
            variants={stagger}
            className="max-w-4xl"
          >
            {/* Enhanced label with decoration */}
            <motion.div 
              variants={fadeIn}
              className="flex items-center gap-4 mb-12"
            >
              <div className="flex items-center gap-3">
                <div className="w-2 h-2 rounded-full bg-primary animate-pulse" />
                <div className="w-10 h-px bg-gradient-to-r from-primary to-transparent" />
              </div>
              <span className="text-[10px] tracking-[0.3em] uppercase text-muted-foreground font-medium">About Us</span>
            </motion.div>

            {/* Word-by-word headline animation */}
            <motion.h1 
              className="font-serif text-4xl sm:text-5xl md:text-6xl lg:text-[4.5rem] tracking-tight leading-[0.95] mb-12"
              aria-label={headline}
            >
              {words.map((word, i) => (
                <motion.span
                  key={i}
                  custom={i}
                  variants={wordVariants}
                  className={`inline-block mr-[0.22em] ${i === words.length - 1 ? 'text-shimmer' : ''}`}
                >
                  {word}
                </motion.span>
              ))}
            </motion.h1>
            
            <motion.p 
              variants={fadeIn}
              className="text-muted-foreground text-lg md:text-xl leading-relaxed max-w-[52ch] mb-12"
              style={{ lineHeight: '1.7' }}
            >
              Sorvaine was founded on a simple observation: the firms that win consistently are not always the ones with the best solutions. They are the ones with the best systems.
            </motion.p>

          </motion.div>
          
          {/* Hero decorative elements */}
          <motion.div 
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.5, duration: 0.8 }}
            className="absolute right-8 md:right-16 lg:right-24 top-1/2 -translate-y-1/2 hidden lg:flex flex-col items-center gap-4"
          >
            {/* Ascending line with glow */}
            <svg width="2" height="200" viewBox="0 0 2 200" className="opacity-40">
              <defs>
                <linearGradient id="lineGradientAbout" x1="0%" y1="100%" x2="0%" y2="0%">
                  <stop offset="0%" stopColor="hsl(var(--primary))" stopOpacity="0" />
                  <stop offset="50%" stopColor="hsl(var(--primary))" stopOpacity="1" />
                  <stop offset="100%" stopColor="hsl(var(--primary))" stopOpacity="0.5" />
                </linearGradient>
              </defs>
              <line 
                x1="1" y1="200" x2="1" y2="0" 
                stroke="url(#lineGradientAbout)" 
                strokeWidth="2"
                className="animate-hero-line"
              />
            </svg>
            
            {/* Floating dots */}
            <div className="flex flex-col gap-3">
              {[0, 1, 2].map((i) => (
                <div 
                  key={i}
                  className="floating-dot"
                  style={{ animationDelay: `${i * 0.5}s` }}
                />
              ))}
            </div>
          </motion.div>
        </div>
        
        {/* Scroll indicator */}
        <AnimatePresence>
          {showScrollIndicator && (
            <motion.div 
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="absolute bottom-8 left-1/2 -translate-x-1/2 flex flex-col items-center gap-2 text-muted-foreground/40"
            >
              <span className="text-[9px] tracking-[0.3em] uppercase">Scroll</span>
              <ChevronDown className="w-4 h-4 scroll-indicator" />
            </motion.div>
          )}
        </AnimatePresence>
      </section>

      {/* Story Section - Premium Grid Layout */}
      <section className="py-28 md:py-36 relative">
        <div className="max-w-[1200px] mx-auto px-6 relative">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: "-100px" }}
            variants={stagger}
            className="grid lg:grid-cols-12 gap-12 lg:gap-24"
          >
            {/* Label with vertical line accent */}
            <motion.div variants={fadeIn} className="lg:col-span-4">
              <div className="lg:sticky lg:top-32">
                <div className="flex items-start gap-5">
                  <div className="w-px h-20 bg-gradient-to-b from-primary/60 to-transparent hidden lg:block" />
                  <div>
                    <span className="text-[11px] tracking-[0.25em] uppercase text-muted-foreground font-medium">Our Story</span>
                    <div className="w-16 h-px bg-gradient-to-r from-border to-transparent mt-5" />
                  </div>
                </div>
              </div>
            </motion.div>

            {/* Main Copy */}
            <motion.div variants={fadeIn} className="lg:col-span-8 space-y-10">
              <h2 className="font-serif text-3xl md:text-4xl lg:text-5xl tracking-tight leading-[1.08]">
                Breaking the cycle of reinvention.
              </h2>
              
              <div className="space-y-8 text-muted-foreground text-base md:text-lg leading-[1.8]">
                <p>
                  Most proposal teams work in a cycle of reinvention. Each new opportunity starts from zero: searching for content that already exists, recreating answers that have already been written, and submitting without clear insight into the strength of the response.
                </p>
                <p>
                  Sorvaine was built to break that cycle. It captures institutional knowledge, learns from every submission, and gives teams the intelligence to compete strategically rather than reactively.
                </p>
              </div>

              <div className="relative pl-8 border-l-2 border-primary/30 corner-accent py-4">
                <blockquote>
                  <p className="font-serif text-xl md:text-2xl italic leading-snug text-foreground/90">
                    The firms that win consistently are not the ones with the best answers.
                    They are the ones with the best systems.
                  </p>
                </blockquote>
                <p className="mt-4 text-sm text-muted-foreground">
                  — Dean Thorpe, Founder
                </p>
              </div>
              
              <p className="text-muted-foreground text-base md:text-lg leading-[1.8]">
                The result is a proposal process that improves with every bid, where past wins inform future strategy and teams can focus on crafting strong, differentiated responses.
              </p>
            </motion.div>
          </motion.div>
        </div>
      </section>





      {/* Values Section */}
      <section className="pt-8 md:pt-12 pb-12 md:pb-16">
        <div className="max-w-[1200px] mx-auto px-6">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: "-100px" }}
            variants={stagger}
          >
            {/* Section header */}
            <motion.div variants={fadeIn} className="mb-20 flex items-center gap-6">
              <div className="flex items-center gap-3">
                <div className="w-2 h-2 rounded-full bg-primary/60" />
                <span className="text-[11px] tracking-[0.25em] uppercase text-muted-foreground font-medium">What We Believe</span>
              </div>
              <div className="flex-1 h-px bg-gradient-to-r from-border/50 to-transparent" />
            </motion.div>

            <TextGradientScroll 
              text="Winning is a system, not luck. Every recommendation should be backed by data. Every insight should be validated by outcomes. That intelligence builds — making each proposal stronger than the last."
              type="word"
              textOpacity="medium"
              className="font-serif tracking-tight leading-[1.4] text-muted-foreground"
            />
          </motion.div>
        </div>
      </section>




      {/* Divider */}
      <div className="max-w-[1200px] mx-auto px-6">
        <div className="divider-glow" />
      </div>

      {/* Final CTA */}
      <section className="pt-16 pb-28 md:pt-20 md:pb-36 relative">
        <div className="max-w-[1200px] mx-auto px-6 text-center relative z-10">
          <motion.div
            initial="visible"
            animate="visible"
            variants={stagger}
            className="space-y-12"
          >
            <motion.h2 
              variants={fadeIn}
              className="font-serif text-4xl md:text-5xl lg:text-6xl tracking-tight text-shimmer"
            >
              Ready to build a better system for winning?
            </motion.h2>
            <motion.p 
              variants={fadeIn}
              className="text-muted-foreground text-lg max-w-lg mx-auto"
            >
              See how Sorvaine helps teams move from reactive bidding to repeatable, strategic wins.
            </motion.p>
            <motion.div variants={fadeIn} className="flex flex-col sm:flex-row items-center justify-center gap-6">
              <Link to="/book-demo">
                <Button className="rounded-md bg-primary hover:bg-primary/90 hover:-translate-y-[1px] hover:scale-[1.03] px-12 py-3 text-base !text-white font-semibold tracking-[0.01em] transition-all duration-200 shadow-[0_0_20px_hsl(var(--primary)/0.15)] hover:shadow-[0_0_32px_hsl(var(--primary)/0.28)]">
                  Book a Demo
                  <ArrowRight className="ml-2 w-5 h-5" />
                </Button>
              </Link>
              <Link to="/contact">
                <button className="inline-flex items-center text-white/[0.40] hover:text-white/[0.65] transition-colors duration-300 text-base font-medium tracking-[0.01em] py-2.5">
                  Contact Us →
                </button>
              </Link>
            </motion.div>
          </motion.div>
        </div>
      </section>

      
    </PublicPageLayout>
  );
}
